import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem } from "./ui/command";
import { Building2, FileText, User } from "lucide-react";

export function CommandPalette({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDq(q), 200);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isFetching } = useQuery({
    queryKey: ["search", dq],
    queryFn: async () => (await api.get(`/search?q=${encodeURIComponent(dq)}`)).data,
    enabled: open && dq.trim().length >= 2,
    staleTime: 30_000,
  });

  const go = (path) => {
    onOpenChange(false);
    nav(path);
  };

  const customers = data?.customers || [];
  const registers = data?.registers || [];
  const employees = data?.employees || [];
  const has = customers.length || registers.length || employees.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 gap-0 sm:max-w-xl">
        <DialogTitle className="sr-only">Search PayDelta</DialogTitle>
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground">
          <CommandInput
            placeholder="Search customers, registers, employees…"
            value={q}
            onValueChange={setQ}
            data-testid="command-input"
          />
          <CommandList className="max-h-[360px]">
            {q.trim().length < 2 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                Type at least 2 characters to search across your customers.
              </div>
            ) : !has && !isFetching ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="command-empty">
                No matches for “{q}”.
              </div>
            ) : null}

            {customers.length > 0 && (
              <CommandGroup heading="Customers">
                {customers.map((c) => (
                  <CommandItem
                    key={c.payroll_client_GUID}
                    value={c.payroll_client_GUID}
                    onSelect={() => go(`/clients/${c.payroll_client_GUID}`)}
                    data-testid="search-result-customer"
                    className="cursor-pointer"
                  >
                    <Building2 size={15} className="mr-2 text-muted-foreground" />
                    <span className="font-medium">{c.client_name}</span>
                    {c.client_short_name ? (
                      <span className="ml-2 text-xs text-muted-foreground">{c.client_short_name}</span>
                    ) : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {registers.length > 0 && (
              <CommandGroup heading="Pay registers">
                {registers.map((r) => (
                  <CommandItem
                    key={r.payroll_run_GUID}
                    value={r.payroll_run_GUID}
                    onSelect={() => go(`/runs/${r.payroll_run_GUID}`)}
                    data-testid="search-result-register"
                    className="cursor-pointer"
                  >
                    <FileText size={15} className="mr-2 text-muted-foreground" />
                    <span className="font-medium">Register {r.run_label}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{r.client_name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {employees.length > 0 && (
              <CommandGroup heading="Employees">
                {employees.map((e) => (
                  <CommandItem
                    key={e.payroll_employee_GUID}
                    value={e.payroll_employee_GUID}
                    onSelect={() =>
                      go(
                        e.latest_run_GUID
                          ? `/runs/${e.latest_run_GUID}/employees/${e.payroll_employee_GUID}`
                          : `/clients/${e.payroll_client_GUID}`,
                      )
                    }
                    data-testid="search-result-employee"
                    className="cursor-pointer"
                  >
                    <User size={15} className="mr-2 text-muted-foreground" />
                    <span className="font-medium">{e.current_employee_name}</span>
                    {e.provider_employee_id ? (
                      <span className="ml-2 font-mono text-xs text-muted-foreground">#{e.provider_employee_id}</span>
                    ) : null}
                    <span className="ml-2 text-xs text-muted-foreground">{e.client_name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
